import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, ExternalLink, ImageOff, X } from 'lucide-react'

import { cn } from '@/lib/cn'

export interface PortfolioItem {
  id: string
  title: string
  description?: string
  url?: string
  image_url?: string
}

/**
 * PortfolioCarousel menampilkan karya sebagai deretan yang digulir menyamping.
 *
 * Dipilih menggantikan kisi karena portofolio dibaca berurutan, bukan
 * dipindai: satu karya besar yang terbaca jelas lebih meyakinkan calon klien
 * daripada dua belas petak kecil yang semuanya setengah terlihat.
 *
 * Pengguliran memakai scroll-snap milik peramban, bukan penggeser yang
 * dihitung sendiri. Dengan begitu usapan di ponsel, roda tetikus, dan tombol
 * panah papan ketik bekerja tanpa satu baris kode pun dari kami.
 */
export function PortfolioCarousel({
  items,
  className,
  renderAction,
}: {
  items: PortfolioItem[]
  className?: string
  /** Tombol tambahan pada tiap kartu, misalnya ganti foto atau hapus. */
  renderAction?: (item: PortfolioItem) => React.ReactNode
}) {
  const track = useRef<HTMLDivElement>(null)
  const [diPangkal, setDiPangkal] = useState(true)
  const [diUjung, setDiUjung] = useState(false)
  const [dibuka, setDibuka] = useState<PortfolioItem | null>(null)

  const perbaruiPosisi = useCallback(() => {
    const el = track.current
    if (!el) return

    setDiPangkal(el.scrollLeft <= 2)
    // Selisih satu piksel dimaafkan: lebar pecahan membuat scrollLeft tidak
    // pernah persis sama dengan batasnya, dan tombolnya akan terlihat aktif
    // padahal sudah mentok.
    setDiUjung(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2)
  }, [])

  useEffect(() => {
    perbaruiPosisi()
  }, [items, perbaruiPosisi])

  const geser = (arah: 1 | -1) => {
    const el = track.current
    if (!el) return

    const kartu = el.firstElementChild as HTMLElement | null
    const langkah = kartu ? kartu.offsetWidth + 16 : el.clientWidth * 0.8
    el.scrollBy({ left: arah * langkah, behavior: 'smooth' })
  }

  if (items.length === 0) return null

  return (
    <div className={cn('relative', className)}>
      <div
        ref={track}
        onScroll={perbaruiPosisi}
        className="thin-scroll flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2"
        // Deretan karya adalah daftar yang dapat ditelusuri papan ketik.
        tabIndex={0}
        role="region"
        aria-label="Portofolio"
      >
        {items.map((item) => (
          <figure
            key={item.id}
            className="group relative w-[17rem] shrink-0 snap-start overflow-hidden rounded-2xl border border-line bg-raised sm:w-[21rem]"
          >
            <button
              type="button"
              onClick={() => setDibuka(item)}
              className="block w-full text-left"
              aria-label={`Lihat ${item.title} lebih besar`}
            >
              {item.image_url ? (
                <img
                  src={item.image_url}
                  alt={item.title}
                  loading="lazy"
                  className="aspect-[4/3] w-full bg-sunken object-cover transition duration-500 group-hover:scale-[1.03]"
                />
              ) : (
                <div className="grid aspect-[4/3] w-full place-items-center bg-sunken text-subtle">
                  <ImageOff className="size-6" />
                </div>
              )}
            </button>

            <figcaption className="space-y-1 p-4">
              <p className="font-bold text-ink">{item.title}</p>
              {item.description ? (
                <p className="line-clamp-2 text-sm text-muted">{item.description}</p>
              ) : null}

              <div className="flex items-center justify-between gap-2 pt-1">
                {item.url ? (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline"
                  >
                    Buka karya
                    <ExternalLink className="size-3.5" />
                  </a>
                ) : (
                  <span />
                )}
                {renderAction?.(item)}
              </div>
            </figcaption>
          </figure>
        ))}
      </div>

      {items.length > 1 ? (
        <>
          <TombolGeser arah="kiri" onClick={() => geser(-1)} disabled={diPangkal} />
          <TombolGeser arah="kanan" onClick={() => geser(1)} disabled={diUjung} />
        </>
      ) : null}

      <Lightbox item={dibuka} onClose={() => setDibuka(null)} />
    </div>
  )
}

function TombolGeser({
  arah,
  onClick,
  disabled,
}: {
  arah: 'kiri' | 'kanan'
  onClick: () => void
  disabled: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={arah === 'kiri' ? 'Karya sebelumnya' : 'Karya berikutnya'}
      className={cn(
        'absolute top-[38%] hidden size-10 -translate-y-1/2 place-items-center rounded-full border border-line bg-surface/90 text-ink shadow-soft backdrop-blur transition sm:grid',
        'hover:bg-surface disabled:pointer-events-none disabled:opacity-0',
        arah === 'kiri' ? '-left-4' : '-right-4',
      )}
    >
      {arah === 'kiri' ? <ChevronLeft className="size-5" /> : <ChevronRight className="size-5" />}
    </button>
  )
}

/**
 * Lightbox menampilkan satu karya sebesar layar.
 *
 * Memakai elemen dialog bawaan peramban lewat komponen sendiri akan
 * menambah lapisan yang tidak diperlukan di sini; yang dibutuhkan hanya
 * lapisan gelap, satu gambar, dan tombol tutup — dengan Esc tetap bekerja.
 */
function Lightbox({ item, onClose }: { item: PortfolioItem | null; onClose: () => void }) {
  useEffect(() => {
    if (!item) return

    const tutupDenganEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', tutupDenganEsc)

    // Halaman di belakang tidak boleh ikut tergulir saat lapisan ini terbuka.
    const semula = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', tutupDenganEsc)
      document.body.style.overflow = semula
    }
  }, [item, onClose])

  if (!item) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={item.title}
      onClick={onClose}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-black/85 p-4 backdrop-blur-sm"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Tutup"
        className="absolute right-4 top-4 grid size-10 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
      >
        <X className="size-5" />
      </button>

      {item.image_url ? (
        <img
          src={item.image_url}
          alt={item.title}
          onClick={(e) => e.stopPropagation()}
          className="max-h-[80vh] max-w-full rounded-xl object-contain"
        />
      ) : null}

      <div className="max-w-xl text-center text-white" onClick={(e) => e.stopPropagation()}>
        <p className="text-lg font-bold">{item.title}</p>
        {item.description ? <p className="mt-1 text-sm text-white/70">{item.description}</p> : null}
      </div>
    </div>
  )
}
